import { ErrorResponse } from './../models/error.model';
import { AWSRequest } from './../models/aws-request.model';
import { Address } from './../models/address.model';
import { Phone } from './../models/phone.model';
import { SortConfiguration } from './../components/generics/data-table/data-table.component';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../dialogs/confirm-dialog/confirm-dialog.component';
import { Router, UrlSerializer } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import * as moment from 'moment'; // npm install moment --save
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { map, distinctUntilChanged } from 'rxjs/operators';
import { AbstractControl, UntypedFormGroup, ValidationErrors, UntypedFormArray, UntypedFormControl } from '@angular/forms';
import { SelectOption } from '../models/select-option.model';

export interface PaginatedResponse<T> {
    data: T;
    totalElementCount: number;
}

export type DrawableType = 'any' | 'text' | 'number' | 'number-2' | 'number-4' | 'date' | 'datetime' | 'money' | 'percent' | 'phone' | 'email' | 'address' | 'resumed-address' | 'link' | 'cuit';

export type StringDict<ValueType = any> = { [key: string]: ValueType };

interface DocumentURLResponse {
    url: string;
    key: string;
    fields: StringDict;
}

@Injectable({
    providedIn: 'root'
})
export class UtilsService {

    private mobile = false;

    constructor(
        private dialog: MatDialog,
        private router: Router,
        private serializer: UrlSerializer,
        private breakpointObserver: BreakpointObserver,
        private http: HttpClient
    ) {
        breakpointObserver.observe(['(max-width: 600px)']).subscribe(result => this.mobile = result.matches);
    }
    downloadBlobAsFile(blob: Blob, filename: string) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    beautifyNumber(n: any, digits: number = 0): string {
        if (n === undefined || n === null) {
            n = 0;
            console.warn(`Mostrando un número como 0 pero en relaidad es ${n}`);
        }
        if (!(n instanceof Number)) {
            n = Number(n);
        }
        if (Number.isInteger(n)) {
            digits = 0;
        }
        return Intl.NumberFormat('es-AR', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n);
    }

    beautifyText(text: any): string {
        if (text) {
            const firstMayus = text.charAt(0).toUpperCase() + text.slice(1);
            return firstMayus.replaceAll('_', ' ');
        }
    }

    beautifyDate(date: string, format = 'LLL'): string {
        const d = moment(date);
        d.locale('es');
        return d.format(format);
    }

    downLoadFile(response: any, filename: string) {
        const dataType = response.type;
        const binaryData = [];
        binaryData.push(response);
        const downloadLink = document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: dataType }));
        downloadLink.setAttribute('download', filename);
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.parentNode.removeChild(downloadLink);
    }

    alert(title: string, description: string) {
        return this.dialog.open(ConfirmDialogComponent, {
            data: {
                titulo: title,
                descripcion: description,
                botonAceptar: 'Aceptar'
            }
        });
    }

    confirm(title: string, description: string) {
        return this.dialog.open(ConfirmDialogComponent, {
            data: {
                titulo: title,
                descripcion: description,
                botonCancelar: 'Cancelar',
                botonAceptar: 'Confirmar'
            }
        });
    }

    serializeUrl(urlArray: any[], queryParams: any) {
        const url = this.router.createUrlTree(urlArray, { queryParams });
        return this.serializer.serialize(url);
    }

    isMobile(): boolean {
        return this.mobile;
    }

    obtenerDecimales(numero: number, cantidadDeDecimales: number): string {
        return Math.floor((numero % 1) * Math.pow(10, cantidadDeDecimales)).toLocaleString('es-AR', { minimumIntegerDigits: cantidadDeDecimales });
    }

    paginatedRequest<T>(url, pageNumber: number, itemsPerPage: number): Observable<PaginatedResponse<T>> {
        return this.http.get<T>(url, {
            observe: 'response', headers: {
                'cantidad-registros': itemsPerPage.toString(),
                'numero-pagina': pageNumber.toString()
            }
        }).pipe(map(response => {
            return {
                data: response.body,
                totalElementCount: Number.parseInt(response.headers.get('total-registros'), 10)
            };
        }));
    }

    generateSortQueryParam(sorts: SortConfiguration[]) {
        return sorts
            .filter(config => !!config.dir && !!config.prop)
            .map(config => (config.dir === 'asc' ? '+' : '-') + config.prop.replace('.', '_'))
            .join(',');
    }

    numberFormValidator(control: AbstractControl): ValidationErrors {
        if (isNaN(control.value)) {
            return { number: true };
        }
        return {};
    }

    autocompleteSelectedValidator(control: AbstractControl): ValidationErrors {
        return typeof control.value === 'object' ? undefined : { notSelected: true };
    }

    drawPhone(phone?: Phone) {
        if (!phone?.codigoPais || !phone?.codigoArea || !phone.numero) return undefined;
        return `+ ${phone.codigoPais} - ${phone.codigoArea} - ${phone.numero}`;
    }

    drawAddress(address?: Address, resumed = false) {
        if (!address) return undefined;
        const tipoCalle = this.beautifyText(address.tipoCalle?.toLowerCase());
        let ret = tipoCalle + ' ' + address.calle + ' ' + address.numero + '\n';
        ret += address.piso ? 'Piso ' + address.piso + ' ' : '';
        ret += address.departamento ? 'Dpto ' + address.departamento + ' ' : '';
        ret += 'CP ' + address.codigoPostal;
        if (address.localidad && !resumed) {
            ret += '\n';
            ret += address.localidad.descripcion + '\n';
            ret += address.localidad.municipio.descripcion + '\n';
            ret += address.localidad.provincia.descripcion;
        }
        return ret;
    }

    parsePhone(phoneString: string) {
        const values = phoneString.split('-').map(x => parseInt(x, 10));
        if (values && values.length === 3 && values.every(x => !isNaN(x))) {
            return {
                codigoPais: values[0],
                codigoArea: values[1],
                numero: values[2]
            } as Phone;
        } else {
            throw new Error(`Error al parsear teléfono, un ejemplo de teléfono correcto es: +54-911-22323223.`);
        }
    }

    stringMatcher(a: string, b: string) {
        return this.removeAccentMarks(a).toLowerCase() === this.removeAccentMarks(b).toLowerCase();
    }

    removeAccentMarks(str: string) {
        return str
            .replace('á', 'a')
            .replace('é', 'e')
            .replace('í', 'i')
            .replace('ó', 'o')
            .replace('ú', 'u')
            .replace('Á', 'A')
            .replace('É', 'E')
            .replace('Í', 'I')
            .replace('Ó', 'O')
            .replace('Ú', 'U');
    }

    compareSelectOption(a: SelectOption, b: SelectOption) {
        return a.codigo === b.codigo;
    }

    uploadFileToAWS<ReturnType>(file: File, getUrlEndpoint: string, reportUploadEndpoint: string, getUrlExtraParams: any = {}, reportUploadExtraParams: any = {}, reportUploadHeaders: any = {}) {
        return new Promise<ReturnType>((resolve, reject) => {
            this.http.post<DocumentURLResponse>(getUrlEndpoint, getUrlExtraParams, { headers: { 'nombre-archivo': file.name, 'bytes-archivo': String(file.size) } }).subscribe(urlData => {
                this.http.put(urlData.url, file).subscribe(_ => {
                    this.http.post<ReturnType>(reportUploadEndpoint, Object.assign({ fileName: file.name, key: urlData.key, url: urlData.url }, reportUploadExtraParams), { headers: reportUploadHeaders }).subscribe(
                        response => resolve(response),
                        (error: ErrorResponse) => reject(error)
                    );
                }, (error: ErrorResponse<any>) => reject(error));
            }, (error: ErrorResponse) => reject(error));
        });
    }

    downloadAWSFile(request: AWSRequest) {
        this.http.get(request.url, { responseType: 'arraybuffer', observe: 'response' }).subscribe(response => {
            this.downLoadFile(response.body, request.fileName);
        });
    }
    //Upload genérico de archivos

    uploadFile<ReturnType>(file: File, getUrlEndpoint: string, reportUploadEndpoint: string, getUrlExtraParams: any = {}, reportUploadExtraParams: any = {}, reportUploadHeaders: any = {}) {
    return new Promise<ReturnType>((resolve, reject) => {
        const formData = new FormData();
        formData.append('file', file, file.name);
        
        console.log('Subiendo archivo:', file.name, 'Tamaño:', file.size);
        
        // POST al endpoint de subida
        this.http.post<any>('/api/files/upload', formData, {            
        }).subscribe(
            uploadResponse => {
                console.log('Archivo subido exitosamente:', uploadResponse);
                
                // POST al endpoint de confirmación de Documento
                this.http.post<ReturnType>(
                    reportUploadEndpoint, 
                    {
                        fileName: uploadResponse.fileName, 
                        key: uploadResponse.key, 
                        url: uploadResponse.url,
                        ...reportUploadExtraParams
                    }, 
                    { headers: reportUploadHeaders }
                ).subscribe(
                    response => {
                        console.log('Confirmación exitosa:', response);
                        resolve(response);
                    },
                    (error: ErrorResponse) => {
                        console.error('Error en confirmación:', error);
                        reject(error);
                    }
                );
            }, 
            (error: ErrorResponse<any>) => {
                console.error('Error subiendo archivo:', error);
                reject(error);
            }
        );
    });
    }
    //Download genérico de archivos
    downloadFile(request: AWSRequest) {
        this.http.get(request.url, { responseType: 'arraybuffer', observe: 'response' }).subscribe(response => {
            this.downLoadFile(response.body, request.fileName);
        });
    }

    parseByType(value: any, type: DrawableType) {
        if (type === 'text') {
            return this.beautifyText(value);
        } else if (type === 'number') {
            return this.beautifyNumber(value, 0);
        } else if (type === 'number-2') {
            return this.beautifyNumber(value, 2);
        } else if (type === 'number-4') {
            return this.beautifyNumber(value, 4);
        } else if (type === 'date') {
            return this.beautifyDate(value, 'L');
        } else if (type === 'datetime') {
            return this.beautifyDate(value, 'L HH:mm');
        } else if (type === 'money') {
            return '$' + this.beautifyNumber(value, 2);
        } else if (type === 'percent') {
            return this.beautifyNumber(value, 2) + '%';
        } else if (type === 'phone') {
            const phone: Phone = value;
            if (!phone) return '';
            return `<a class="link" href="tel:+${phone.codigoPais}${phone.codigoArea}${phone.numero}">${this.drawPhone(phone)}</a>`;
        } else if (type === 'email') {
            return value ? `<a class="link" href="mailto:${value}">${value}</a>` : '';
        } else if (type === 'address' || type === 'resumed-address') {
            if (value && value.calle && value.numero) {
                return `<a class="link" href="https://maps.google.com/?q=${value.calle} ${value.numero}, ${value.localidad?.descripcion ?? ''}, ${value.localidad?.provincia?.descripcion ?? ''}" target="_blank">${this.drawAddress(value, type === 'resumed-address')}</a>`;
            }
            return '';
        } else if (type === 'link') {
            return `<a class="link" href="${value.href}" target="_blank">${value.label}</a>`;
        } else if (type === 'cuit') {
            if (typeof value === 'string' && value.length === 11)
                return value.substring(0, 2) + '-' + value.substring(2, 10) + '-' + value.substring(10, 11);
            return value;
        }
        return value;
    }

    convertEveryWordFirstLetterToUppercase(text: string) {
        return text?.split(' ').map(word => word.length ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : word).join(' ');
    }

    formControlUppercasedFirstLetterOfEveryWord(control: AbstractControl) {
        control.valueChanges
            .pipe(distinctUntilChanged())
            .subscribe((value: string) => control.patchValue(this.convertEveryWordFirstLetterToUppercase(value)));
    }

    fillFormWithPartialData(form: AbstractControl, data: any, emitEvent = true) {
        if (form instanceof UntypedFormControl) {
            form.setValue(data ?? null, { emitEvent });
        } else if (form instanceof UntypedFormGroup) {
            for (const control of Object.keys(form.controls)) {
                this.fillFormWithPartialData(form.controls[control], data && data[control] ? data[control] : null);
            }
        } else if (form instanceof UntypedFormArray) {
            form.controls.forEach((control, index) => {
                this.fillFormWithPartialData(control, data && data[index] ? data[index] : null);
            });
        }
    }

    disableOrEnableFormGroupRecursive(control: AbstractControl, action: 'enable' | 'disable', exceptions: AbstractControl[] = []) {
        if (control instanceof UntypedFormControl) {
            control[action]({ emitEvent: false });
        } else if (control instanceof UntypedFormGroup) {
            for (const current of Object.keys(control.controls)) {
                if (exceptions.includes(control.controls[current])) continue;
                this.disableOrEnableFormGroupRecursive(control.controls[current], action, exceptions);
            }
        } else if (control instanceof UntypedFormArray) {
            control.controls.forEach((current, index) => {
                this.disableOrEnableFormGroupRecursive(control, action, exceptions);
            });
        }
    }

    camelCaseToText(camel: string) {
        const result = camel.replace(/([A-Z])/g, ' $1').trim();
        return result.charAt(0).toUpperCase() + result.slice(1).toLowerCase();
    }
}
